// Run detail — phase timeline + plan + reviewer notes + logs.
// Polls ~5s while the run is live; stops once it reaches a terminal state.

import { useCallback, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import api from "../../api";
import useBssPoll from "../../hooks/useBssPoll";
import Icon from "./icons";
import Shell from "./Shell";
import AssignPrModal from "./AssignPrModal";
import { AutoVerdict } from "./BrowserVerifyEvidence";
import { ReleaseCutPanel } from "./ReleaseCutPanel";
import {
  Avatar,
  ErrorState,
  LoadingState,
  Html,
  LogView,
  Markdown,
  PhaseGlyph,
  ProgressBar,
  RefreshButton,
  Spinner,
  StatusBadge,
  apiErrorMessage,
  formatDuration,
  formatMoney,
  heroStatusClass,
  isAwaitingApproval,
  isLive,
  phaseStatusClass,
  phaseStripColor,
  relativeTime,
  PHASE_COLOR,
  PHASE_ICON
} from "./shared";

const BODY_TAB_LABEL = { Reviewer: "Review notes", Planner: "Plan" };
const bodyTabLabel = (name) => BODY_TAB_LABEL[name] || "Output";

const MODE_LABELS = {
  test_plan: "Test plan",
  adoption: "Adopted PR",
  refresh: "Refresh",
  "address-comments": "Address comments",
  "address-checks": "Address checks",
  review: "PR review",
  "browser-verify": "Browser QA",
  "release-cut": "Release cut",
  architect: "Architect scoping",
  implement: "Implement"
};

// Ticketless review runs are keyed by a PR ref (e.g. `boompay-client#2994`) with
// no Jira ticket behind it. The `#` is the marker — when a run's key is a PR
// ref, the ticket affordances (breadcrumb deep-link, "All runs", "Assign PR")
// don't apply: /bss/tickets/<pr-ref> 404s.
const isTicketKey = (k) => !!k && !k.includes("#");

const PhaseStatusBadge = ({ status }) => {
  if (status === "running")
    return (
      <span
        className="bss-badge bss-badge--pink bss-badge--pulsing"
        style={{ marginLeft: 6 }}
      >
        <span className="bss-dot" />
        Running
      </span>
    );
  if (status === "failed")
    return (
      <span className="bss-badge bss-badge--red" style={{ marginLeft: 6 }}>
        <span className="bss-dot" />
        Failed
      </span>
    );
  return null;
};

// Cross-run flow memory: the structured note this run wrote at completion for
// LATER runs on the same PR to read (so address-comments / refresh / review
// pick up prior context instead of treating the PR as fresh). This card lets a
// human see exactly what continuity each run contributed.
const FLOW_LABEL = {
  fontSize: 11.5,
  color: "var(--bss-fg-subtle)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: 6
};

const FlowNoteCard = ({ note }) => {
  if (!note || typeof note !== "object") return null;
  const decisions = Array.isArray(note.decisions) ? note.decisions : [];
  const openThreads = Array.isArray(note.openThreads) ? note.openThreads : [];
  return (
    <div
      className="bss-card"
      data-testid="flow-note"
      style={{ padding: 20, marginBottom: 24 }}
    >
      <div
        className="bss-card-hd"
        style={{ display: "flex", alignItems: "center", gap: 10, padding: 0, marginBottom: 14 }}
      >
        <Icon.GitBranch size={14} />
        <h3
          style={{
            fontSize: 13,
            color: "var(--bss-fg-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.06em"
          }}
        >
          Flow note — cross-run memory
        </h3>
      </div>

      {note.outcome && (
        <div style={{ marginBottom: 14 }}>
          <div style={FLOW_LABEL}>Outcome</div>
          <div style={{ fontSize: 13.5, color: "var(--bss-fg-strong)", lineHeight: 1.5 }}>
            {note.outcome}
          </div>
        </div>
      )}

      {note.summary && (
        <div style={{ marginBottom: decisions.length || openThreads.length ? 16 : 0 }}>
          <div style={FLOW_LABEL}>What this run did</div>
          <div style={{ fontSize: 13.5, color: "var(--bss-fg-strong)", lineHeight: 1.55 }}>
            <Markdown src={note.summary} />
          </div>
        </div>
      )}

      {decisions.length > 0 && (
        <div style={{ marginBottom: openThreads.length ? 16 : 0 }}>
          <div style={FLOW_LABEL}>Decisions</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.55 }}>
            {decisions.map((d, i) => (
              <li key={i} style={{ color: "var(--bss-fg-strong)" }}>{d}</li>
            ))}
          </ul>
        </div>
      )}

      {openThreads.length > 0 && (
        <div>
          <div style={FLOW_LABEL}>Open threads</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.55 }}>
            {openThreads.map((t, i) => (
              <li key={i} style={{ color: "var(--bss-fg-strong)" }}>{t}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Figma design the architect SAW while scoping — proof of what it took into
// account. The rendered screen(s) (presigned S3 urls) + the Figma deep link +
// the extracted copy. Present on every run in a group whose ticket linked a
// design (architect run + its fanned-out implement runs).
const DesignProofCard = ({ design }) => {
  if (!design || typeof design !== "object") return null;
  const links = Array.isArray(design.links) ? design.links : [];
  const images = Array.isArray(design.images) ? design.images : [];
  const text = typeof design.text === "string" ? design.text : "";
  const mismatch = typeof design.mismatch === "string" ? design.mismatch : "";
  if (links.length === 0 && images.length === 0 && !text.trim() && !mismatch)
    return null;
  return (
    <div
      className="bss-card"
      data-testid="design-proof"
      style={{ padding: 20, marginBottom: 24 }}
    >
      <div
        className="bss-card-hd"
        style={{ display: "flex", alignItems: "center", gap: 10, padding: 0, marginBottom: 14 }}
      >
        <Icon.Eye size={14} />
        <h3
          style={{
            fontSize: 13,
            color: "var(--bss-fg-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.06em"
          }}
        >
          Design context — what the architect saw
        </h3>
      </div>

      {mismatch && (
        <div
          data-testid="design-mismatch"
          style={{
            marginBottom: 16,
            padding: "10px 12px",
            borderRadius: 6,
            border: "1px solid var(--bss-warning-500, #b45309)",
            background: "var(--bss-warning-50, rgba(180,83,9,0.08))"
          }}
        >
          <div style={{ ...FLOW_LABEL, color: "var(--bss-warning-700, #b45309)" }}>
            Design / ticket mismatch (posted to Jira)
          </div>
          <div style={{ fontSize: 13.5, color: "var(--bss-fg-strong)", lineHeight: 1.5 }}>
            {mismatch}
          </div>
        </div>
      )}

      {links.length > 0 && (
        <div style={{ marginBottom: images.length || text.trim() ? 16 : 0 }}>
          <div style={FLOW_LABEL}>Figma screen(s)</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
            {links.map((l, i) => (
              <li key={i} style={{ color: "var(--bss-fg-strong)" }}>
                <a href={l.url} target="_blank" rel="noreferrer">
                  {l.screenName || l.url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {images.length > 0 && (
        <div style={{ marginBottom: text.trim() ? 16 : 0 }}>
          <div style={FLOW_LABEL}>Rendered design</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 6 }}>
            {images.map((im, i) =>
              im && im.url ? (
                <a key={i} href={im.url} target="_blank" rel="noreferrer">
                  <img
                    src={im.url}
                    alt={`design ${i + 1}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: 420,
                      border: "1px solid var(--bss-border)",
                      borderRadius: 6
                    }}
                  />
                </a>
              ) : (
                <div
                  key={i}
                  style={{ fontSize: 12.5, color: "var(--bss-fg-subtle)" }}
                >
                  (image stored but not viewable — S3 link unavailable)
                </div>
              )
            )}
          </div>
        </div>
      )}

      {text.trim() && (
        <details>
          <summary
            style={{
              fontSize: 11.5,
              color: "var(--bss-fg-subtle)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              cursor: "pointer"
            }}
          >
            Extracted copy / structure
          </summary>
          <pre
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              marginTop: 8,
              whiteSpace: "pre-wrap",
              color: "var(--bss-fg-strong)"
            }}
          >
            {text}
          </pre>
        </details>
      )}
    </div>
  );
};

// The Slack discussion linked from the ticket that the agents were shown
// (text-only transcript + source permalinks). Mirrors DesignProofCard so a
// reviewer can confirm the agent actually saw the conversation.
const SlackDiscussionCard = ({ slack }) => {
  if (!slack || typeof slack !== "object") return null;
  const links = Array.isArray(slack.links) ? slack.links : [];
  const text = typeof slack.text === "string" ? slack.text : "";
  const threadCount =
    typeof slack.threadCount === "number" && slack.threadCount > 0
      ? slack.threadCount
      : links.length;
  if (links.length === 0 && !text.trim()) return null;
  return (
    <div
      className="bss-card"
      data-testid="slack-discussion"
      style={{ padding: 20, marginBottom: 24 }}
    >
      <div
        className="bss-card-hd"
        style={{ display: "flex", alignItems: "center", gap: 10, padding: 0, marginBottom: 14 }}
      >
        <Icon.MessageSquare size={14} />
        <h3
          style={{
            fontSize: 13,
            color: "var(--bss-fg-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.06em"
          }}
        >
          Slack discussion — what the agent saw
        </h3>
      </div>

      {links.length > 0 && (
        <div style={{ marginBottom: text.trim() ? 16 : 0 }}>
          <div style={FLOW_LABEL}>
            {threadCount === 1 ? "Source thread" : `Source threads (${threadCount})`}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
            {links.map((l, i) => (
              <li key={i} style={{ color: "var(--bss-fg-strong)", wordBreak: "break-all" }}>
                <a href={l.url} target="_blank" rel="noreferrer">
                  {l.url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {text.trim() && (
        <details open>
          <summary
            style={{
              fontSize: 11.5,
              color: "var(--bss-fg-subtle)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              cursor: "pointer"
            }}
          >
            Conversation transcript
          </summary>
          <pre
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              marginTop: 8,
              whiteSpace: "pre-wrap",
              color: "var(--bss-fg-strong)"
            }}
          >
            {text}
          </pre>
        </details>
      )}
    </div>
  );
};

// Reporter-attached ticket images (screenshots/photos) the agents were shown,
// presigned for display. Mirrors DesignProofCard's thumbnail render.
const TicketMediaCard = ({ media }) => {
  if (!media || typeof media !== "object") return null;
  const items = Array.isArray(media.items) ? media.items : [];
  if (items.length === 0) return null;
  return (
    <div className="bss-card" data-testid="ticket-media" style={{ padding: 20, marginBottom: 24 }}>
      <div
        className="bss-card-hd"
        style={{ display: "flex", alignItems: "center", gap: 10, padding: 0, marginBottom: 14 }}
      >
        <Icon.FileText size={14} />
        <h3
          style={{
            fontSize: 13,
            color: "var(--bss-fg-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.06em"
          }}
        >
          Ticket media — reporter screenshots
        </h3>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {items.map((it, i) =>
          it && it.url ? (
            <a key={i} href={it.url} target="_blank" rel="noreferrer" title={it.name || ""}>
              <img
                src={it.url}
                alt={it.name || `ticket image ${i + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: 420,
                  border: "1px solid var(--bss-border)",
                  borderRadius: 6
                }}
              />
            </a>
          ) : (
            <div key={i} style={{ fontSize: 12.5, color: "var(--bss-fg-subtle)" }}>
              {it && it.name ? `${it.name}: ` : ""}(image stored but not viewable — S3 link unavailable)
            </div>
          )
        )}
      </div>
    </div>
  );
};

// Full review-pipeline breakdown (review runs only): the fan-out → verifier →
// quote-check → refute funnel, each lens's work, the posted survivors, and the
// findings each gate rejected (+ why). All from the persisted run.reviewFindings.
const SEV_TINT = {
  critical: "var(--bss-danger)",
  high: "var(--bss-danger)",
  medium: "var(--bss-info)",
  low: "var(--bss-fg-muted)"
};
const STAGE_LABEL = {
  verifier: "Dropped by verifier",
  quote_check: "Dropped by quote-check",
  refuted: "Dropped by refute panel"
};
const RB_LABEL = {
  fontSize: 11,
  color: "var(--bss-fg-subtle)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: 8
};

const FindingRow = ({ f, muted }) => (
  <div style={{ padding: "9px 0", borderBottom: "1px solid var(--bss-border)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 11.5 }}>
      {f.severity && (
        <span
          className="bss-badge"
          style={{ background: SEV_TINT[f.severity] || "var(--bss-fg-muted)", color: "#fff", textTransform: "uppercase" }}
        >
          {f.severity}
        </span>
      )}
      {f.lens && <span style={{ color: "var(--bss-fg-subtle)" }}>{f.lens} lens</span>}
      {f.path && (
        <span style={{ fontFamily: "var(--bss-font-mono)", color: "var(--bss-fg-muted)" }}>
          {f.path}
          {f.line ? `:${f.line}` : ""}
        </span>
      )}
    </div>
    {f.title && (
      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--bss-fg-strong)", marginTop: 4 }}>
        {f.title}
      </div>
    )}
    {f.body && (
      <div style={{ fontSize: 12.5, marginTop: 2, lineHeight: 1.5, color: muted ? "var(--bss-fg-muted)" : "var(--bss-fg)" }}>
        <Markdown src={f.body} />
      </div>
    )}
    {f.quote && (
      <div
        style={{
          fontFamily: "var(--bss-font-mono)",
          fontSize: 11.5,
          color: "var(--bss-fg-muted)",
          background: "var(--bss-surface-2)",
          borderRadius: 4,
          padding: "4px 8px",
          marginTop: 6,
          overflowX: "auto"
        }}
      >
        {f.quote}
      </div>
    )}
    {f.reason && (
      <div style={{ fontSize: 11.5, marginTop: 5, color: "var(--bss-fg-subtle)" }}>↳ {f.reason}</div>
    )}
  </div>
);

const FunnelStep = ({ label, value, color }) => (
  <div style={{ textAlign: "center" }}>
    <div style={{ fontSize: 24, fontWeight: 600, color, fontFamily: "var(--bss-font-mono)" }}>{value}</div>
    <div style={{ fontSize: 10.5, color: "var(--bss-fg-muted)" }}>{label}</div>
  </div>
);

function ReviewBreakdownCard({ findings }) {
  if (!findings || typeof findings !== "object") return null;
  // The SS sends review telemetry under `_meta`, but the shared axios instance
  // runs humps.camelizeKeys on every response, which STRIPS the leading
  // underscore (`_meta` -> `meta`). Read the camelized key first (what the FE
  // actually receives), with `_meta` as a defensive fallback for any
  // non-camelized path. Without this the whole funnel + the design row read an
  // undefined `_meta` and silently show zeros / nothing.
  const meta = findings.meta || findings._meta || {};
  const comments = Array.isArray(findings.comments) ? findings.comments : [];
  const advisories = Array.isArray(findings.advisories) ? findings.advisories : [];
  const lenses = Array.isArray(findings.lensBreakdown) ? findings.lensBreakdown : [];
  const dropped = Array.isArray(findings.dropped) ? findings.dropped : [];

  const keptByLens = {};
  for (const c of comments) keptByLens[c.lens || "?"] = (keptByLens[c.lens || "?"] || 0) + 1;
  for (const a of advisories) keptByLens[a.lens || "?"] = (keptByLens[a.lens || "?"] || 0) + 1;

  const droppedByStage = {};
  for (const d of dropped) {
    if (!droppedByStage[d.stage]) droppedByStage[d.stage] = [];
    droppedByStage[d.stage].push(d);
  }

  return (
    <div className="bss-card" data-testid="review-breakdown" style={{ padding: 20, marginBottom: 24 }}>
      <div className="bss-card-hd" style={{ display: "flex", alignItems: "center", gap: 10, padding: 0, marginBottom: 16 }}>
        <Icon.Eye size={14} />
        <h3 style={{ fontSize: 13, color: "var(--bss-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Review breakdown
        </h3>
      </div>

      {/* Pipeline funnel */}
      <div style={{ marginBottom: 18 }}>
        <div style={RB_LABEL}>Pipeline</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <FunnelStep label="Candidates" value={meta.candidates || 0} color="var(--bss-fg-muted)" />
          <span style={{ color: "var(--bss-fg-subtle)" }}>→</span>
          <FunnelStep label="Verified" value={meta.verified || 0} color="var(--bss-info)" />
          <span style={{ color: "var(--bss-fg-subtle)" }}>→</span>
          <FunnelStep label="Posted (blocking)" value={meta.kept != null ? meta.kept : comments.length} color="var(--bss-boom-pink)" />
          <span style={{ marginLeft: 18, fontSize: 12, color: "var(--bss-fg-muted)" }}>
            {meta.refuterRan ? `refute: ${meta.refuteDemoted || 0} demoted · ${meta.refuteDropped || 0} dropped · ` : ""}
            {advisories.length} advisor{advisories.length === 1 ? "y" : "ies"}
          </span>
        </div>
      </div>

      {/* Design grounding — was the Figma design actually in front of the reviewer? */}
      {"designUsed" in meta && (
        <div style={{ marginBottom: 18 }}>
          <div style={RB_LABEL}>Design</div>
          {meta.designUsed ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 10px",
                borderRadius: 6,
                fontSize: 12.5,
                color: "var(--bss-fg-strong)",
                background: "rgba(76,175,80,0.12)",
                border: "1px solid rgba(76,175,80,0.4)"
              }}
            >
              <span>✓</span>
              Reviewed against design
              {Array.isArray(meta.designScreens) && meta.designScreens.length > 0
                ? `: ${meta.designScreens.join(", ")}`
                : ""}
            </span>
          ) : (
            <span style={{ fontSize: 12.5, color: "var(--bss-fg-muted)" }}>
              No design staged — this PR was reviewed without a Figma reference.
            </span>
          )}
        </div>
      )}

      {/* Per-lens */}
      {lenses.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div style={RB_LABEL}>Per-lens (fan-out)</div>
          {lenses.map((l) => {
            const proposed = (l.proposedComments || 0) + (l.proposedAdvisories || 0);
            const kept = keptByLens[l.lens] || 0;
            return (
              <div key={l.lens} style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "6px 0", fontSize: 12.5, borderBottom: "1px solid var(--bss-border)" }}>
                <span style={{ width: 150, color: "var(--bss-fg-strong)", fontWeight: 500 }}>
                  {l.label || l.lens}
                  {!l.ok && <span style={{ color: "var(--bss-danger)" }}> · failed</span>}
                </span>
                <span style={{ width: 150, color: "var(--bss-fg-muted)", fontFamily: "var(--bss-font-mono)", fontSize: 11.5 }}>
                  {proposed} proposed · {kept} kept
                </span>
                <span style={{ flex: 1, color: "var(--bss-fg-muted)", fontSize: 12 }}>{l.summary || "(no findings)"}</span>
                {l.costUsd != null && (
                  <span style={{ color: "var(--bss-fg-subtle)", fontFamily: "var(--bss-font-mono)", fontSize: 11.5 }}>
                    ${(l.costUsd || 0).toFixed(2)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Posted findings */}
      {(comments.length > 0 || advisories.length > 0) && (
        <div style={{ marginBottom: dropped.length ? 18 : 0 }}>
          <div style={RB_LABEL}>Posted findings ({comments.length + advisories.length})</div>
          {comments.map((c, i) => (
            <FindingRow key={`c${i}`} f={c} />
          ))}
          {advisories.map((a, i) => (
            <FindingRow key={`a${i}`} f={{ ...a, severity: a.severity || "low" }} muted />
          ))}
        </div>
      )}

      {/* Rejected — collapsible */}
      {dropped.length > 0 && (
        <details>
          <summary style={{ ...RB_LABEL, cursor: "pointer", marginBottom: 0 }}>
            Rejected — not posted ({dropped.length})
          </summary>
          <div style={{ marginTop: 10 }}>
            {Object.keys(droppedByStage).map((stage) => (
              <div key={stage} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "var(--bss-fg-muted)", fontWeight: 500, marginBottom: 4 }}>
                  {STAGE_LABEL[stage] || stage} ({droppedByStage[stage].length})
                </div>
                {droppedByStage[stage].map((d, i) => (
                  <FindingRow key={`${stage}${i}`} f={d} muted />
                ))}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

const PhaseBody = ({ phase, tab, hasBody, hasLog }) => {
  if (tab === "log" && hasLog)
    return <LogView rows={phase.log} live={phase.status === "running"} />;
  if (tab !== "body" || !hasBody) return null;
  if (phase.body.kind === "error")
    return (
      <div
        className="bss-error-block"
        style={{ margin: 0, border: 0, borderRadius: 0 }}
      >
        <div className="bss-hd">
          <Icon.AlertTriangle size={13} />
          Phase terminated
        </div>
        {phase.body.content}
      </div>
    );
  // HTML bodies are server-sanitized; Html re-sanitizes as defense in depth.
  if (phase.body.kind === "html") return <Html src={phase.body.content} />;
  // Safe fallback for a 'markdown'-kind body.
  return <Markdown src={phase.body.content} />;
};

const PhaseRow = ({ phase }) => {
  const hasBody = !!(phase.body && phase.body.content);
  const hasLog = !!(phase.log && phase.log.length > 0);
  const expandable = hasBody || hasLog;
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState(hasBody ? "body" : "log");
  const PhaseIcon = PHASE_ICON[phase.name] || Icon.Code;
  const pending = phase.status === "pending";

  return (
    <div className={`bss-phase ${phaseStatusClass(phase.status)}`}>
      <div className="bss-phase-dot">
        <PhaseGlyph status={phase.status} />
      </div>
      <div
        className={`bss-phase-row ${open ? "is-open" : ""}`.trim()}
        onClick={() => expandable && setOpen(!open)}
      >
        <span
          className="bss-phase-name"
          style={{
            color: pending ? "var(--bss-fg-muted)" : "var(--bss-fg-strong)"
          }}
        >
          {expandable && <Icon.ChevronRight size={12} className="bss-caret" />}
          <PhaseIcon size={14} style={{ color: PHASE_COLOR[phase.name] }} />
          {phase.name}
          <PhaseStatusBadge status={phase.status} />
          {phase.summary && phase.status !== "running" && (
            <span
              style={{
                fontWeight: 400,
                color: "var(--bss-fg-muted)",
                fontSize: 12.5,
                marginLeft: 6
              }}
            >
              · {phase.summary}
            </span>
          )}
        </span>
        <span className="bss-phase-meta">
          {phase.durationSec != null && (
            <span>
              <Icon.Clock
                size={11}
                style={{
                  verticalAlign: -2,
                  marginRight: 4,
                  color: "var(--bss-fg-subtle)"
                }}
              />
              {formatDuration(phase.durationSec)}
            </span>
          )}
          {phase.costUsd != null && (
            <span className="bss-cost-line">{formatMoney(phase.costUsd)}</span>
          )}
        </span>
      </div>

      {open && expandable && (
        <div className="bss-phase-body">
          <div className="bss-phase-body-tabs">
            {hasBody && (
              <button
                type="button"
                className={`bss-phase-tab ${
                  tab === "body" ? "is-active" : ""
                }`.trim()}
                onClick={() => setTab("body")}
              >
                {bodyTabLabel(phase.name)}
              </button>
            )}
            {hasLog && (
              <button
                type="button"
                className={`bss-phase-tab ${
                  tab === "log" ? "is-active" : ""
                }`.trim()}
                onClick={() => setTab("log")}
              >
                Logs
                <span
                  style={{
                    color: "var(--bss-fg-subtle)",
                    marginLeft: 4
                  }}
                >
                  · {phase.log.length}
                </span>
              </button>
            )}
          </div>
          <PhaseBody
            phase={phase}
            tab={tab}
            hasBody={hasBody}
            hasLog={hasLog}
          />
        </div>
      )}
    </div>
  );
};

const LENS_META = {
  logical: { label: "Logical / flow", color: "var(--bss-boom-pink)" },
  correctness: { label: "Correctness", color: "var(--bss-info)" },
  security: { label: "Security", color: "var(--bss-danger)" },
  contract: { label: "API / contract", color: "var(--bss-warning)" },
  performance: { label: "Performance", color: "var(--bss-success)" },
  walk: { label: "Advisory walk", color: "var(--bss-fg-subtle)" }
};

// Per-lens fan-out breakdown — what each specialized reviewer agent did:
// did it run, what it PROPOSED (pre-verifier), what was KEPT (post-verifier),
// its cost, and its take. The proposed→kept gap is the per-lens precision
// signal a human uses to tune lenses. Renders only when the run carries a
// `lensBreakdown` (review runs on the fan-out path).
// Browser-QA runs: the per-segment verdicts + evidence recordings ARE the
// run's output — render them on the detail page (the QA Workspace shows the
// same evidence in worksheet context). Self-hides for every other run mode.
function BrowserVerifyVerdicts({ browserVerify, ticketKey }) {
  const executions =
    browserVerify && Array.isArray(browserVerify.executions)
      ? browserVerify.executions
      : [];
  if (executions.length === 0) return null;
  // Release-test runs use a synthetic per-app issueKey "<releaseKey>::<app>" —
  // there's no per-ticket QA workspace for it, so don't link to one.
  const isReleaseTest = ticketKey.includes("::");
  return (
    <div className="bss-card" style={{ overflow: "hidden", marginTop: 14 }}>
      <div className="bss-card-hd">
        <Icon.PlayCircle size={14} />
        Browser QA — verdicts &amp; recordings
        <span
          style={{
            color: "var(--bss-fg-subtle)",
            marginLeft: 8,
            fontWeight: 400,
            fontSize: 12
          }}
        >
          {isReleaseTest ? (
            "one recorded segment per flow"
          ) : (
            <>
              one recorded segment per test — full worksheet on the{" "}
              <Link to={`/bss/tickets/${encodeURIComponent(ticketKey)}/qa`}>
                QA workspace
              </Link>
            </>
          )}
        </span>
      </div>
      <div style={{ padding: "4px 16px 14px" }}>
        {executions.map((e) => (
          <div
            key={e.stableKey}
            style={{
              padding: "10px 0",
              borderBottom: "1px solid var(--bss-border)"
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>
              {e.title}
              <span
                style={{
                  color: "var(--bss-fg-subtle)",
                  fontWeight: 400,
                  fontSize: 11.5,
                  marginLeft: 8
                }}
              >
                {e.durationSec != null ? `${e.durationSec}s` : ""}
                {e.costUsd != null ? ` · $${Number(e.costUsd).toFixed(2)}` : ""}
              </span>
            </div>
            <AutoVerdict execution={e} />
          </div>
        ))}
      </div>
    </div>
  );
}

function LensBreakdown({ findings }) {
  const rows =
    findings && Array.isArray(findings.lensBreakdown)
      ? findings.lensBreakdown
      : [];
  if (rows.length === 0) return null;
  const kept = {};
  const bump = (lens, key) => {
    const l = lens || "—";
    kept[l] = kept[l] || { comments: 0, advisories: 0 };
    kept[l][key] += 1;
  };
  (findings.comments || []).forEach((c) => bump(c.lens, "comments"));
  (findings.advisories || []).forEach((a) => bump(a.lens, "advisories"));
  const mono = { fontFamily: "var(--bss-font-mono)", fontSize: 12 };
  return (
    <div className="bss-card" style={{ overflow: "hidden", marginTop: 14 }}>
      <div className="bss-card-hd">
        <Icon.Eye size={14} />
        Lens breakdown
        <span
          style={{
            color: "var(--bss-fg-subtle)",
            marginLeft: 8,
            fontWeight: 400,
            fontSize: 12
          }}
        >
          each specialized reviewer — proposed → kept (after the verifier)
        </span>
      </div>
      <table className="bss-table">
        <thead>
          <tr>
            <th>Lens</th>
            <th style={{ textAlign: "center", width: 50 }}>Ran</th>
            <th style={{ textAlign: "right", width: 90 }}>Proposed</th>
            <th style={{ textAlign: "right", width: 90 }}>Kept</th>
            <th style={{ textAlign: "right", width: 70 }}>Cost</th>
            <th>Summary</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const meta = LENS_META[r.lens] || {
              label: r.lens,
              color: "var(--bss-fg-subtle)"
            };
            const k = kept[r.lens] || { comments: 0, advisories: 0 };
            return (
              <tr key={r.lens}>
                <td>
                  <span
                    style={{ color: meta.color, fontWeight: 600, fontSize: 12 }}
                  >
                    {meta.label}
                  </span>
                </td>
                <td
                  style={{
                    textAlign: "center",
                    color: r.ok ? "var(--bss-success)" : "var(--bss-danger)"
                  }}
                >
                  {r.ok ? "✓" : "✗"}
                </td>
                <td style={{ ...mono, textAlign: "right" }}>
                  {r.proposedComments}c · {r.proposedAdvisories}a
                </td>
                <td style={{ ...mono, textAlign: "right" }}>
                  {k.comments}c · {k.advisories}a
                </td>
                <td style={{ ...mono, textAlign: "right" }}>
                  {formatMoney(r.costUsd)}
                </td>
                <td style={{ color: "var(--bss-fg-muted)", fontSize: 12 }}>
                  {r.summary}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function RunDetailPage() {
  const { id } = useParams();
  const history = useHistory();
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const fetchRun = useCallback(
    () => api.bssRun(id).then((res) => res.data),
    [id]
  );
  const { data: run, error, loading, refresh } = useBssPoll(fetchRun);

  const live = run && isLive(run.status);
  const awaitingApproval = run && isAwaitingApproval(run.status);
  const phases = useMemo(() => (run && run.phases) || [], [run]);
  const totalCost = useMemo(() => {
    if (!run) return 0;
    const summed = phases.reduce(
      (s, p) => s + (p.costUsd != null ? p.costUsd : 0),
      0
    );
    return summed || run.costUsd || 0;
  }, [run, phases]);

  const cancelRun = useCallback(() => {
    setBusy(true);
    setActionError(null);
    api
      .bssCancelRun(id)
      .then(() => {
        setBusy(false);
        refresh();
      })
      .catch((err) => {
        setBusy(false);
        setActionError(apiErrorMessage(err, "Could not cancel the run."));
      });
  }, [id, refresh]);

  const approveRun = useCallback(() => {
    setBusy(true);
    setActionError(null);
    api
      .bssApproveRun(id)
      .then(() => {
        setBusy(false);
        refresh();
      })
      .catch((err) => {
        setBusy(false);
        setActionError(apiErrorMessage(err, "Could not approve the run."));
      });
  }, [id, refresh]);

  const retryRun = useCallback(
    (mode) => {
      setBusy(true);
      setActionError(null);
      api
        .bssRetryRun({ id, mode })
        .then((res) => {
          setBusy(false);
          if (res.data && res.data.id) history.push(`/bss/runs/${res.data.id}`);
          else refresh();
        })
        .catch((err) => {
          setBusy(false);
          setActionError(apiErrorMessage(err, "Could not retry the run."));
        });
    },
    [id, history, refresh]
  );

  if (loading) {
    return (
      <Shell>
        <LoadingState label="Loading run…" />
      </Shell>
    );
  }
  if (error || !run) {
    return (
      <Shell>
        <ErrorState
          message={apiErrorMessage(error, "Run not found.")}
          onRetry={refresh}
        />
      </Shell>
    );
  }

  const heroCls = heroStatusClass(run.status);
  const budgetPct = run.budgetUsd ? totalCost / run.budgetUsd : 0;

  return (
    <Shell>
      <div className="bss-crumb">
        <button
          type="button"
          className="bss-btn bss-btn--ghost bss-btn--sm"
          onClick={() => history.push("/bss")}
        >
          <Icon.ChevronLeft size={13} /> All runs
        </button>
        <span>/</span>
        {isTicketKey(run.key) ? (
          <button
            type="button"
            className="bss-btn bss-btn--ghost bss-btn--sm"
            onClick={() =>
              history.push(
                `/bss/tickets/${encodeURIComponent(run.key)}`
              )
            }
          >
            <Icon.Ticket size={13} /> {run.key}
          </button>
        ) : (
          // Ticketless review: no Jira ticket — show the PR ref as a plain label.
          <span style={{ fontFamily: "var(--bss-font-mono)" }}>{run.key}</span>
        )}
        <span>/</span>
        <span style={{ fontFamily: "var(--bss-font-mono)" }}>{run.id}</span>
        <span style={{ marginLeft: "auto", display: "inline-flex", gap: 8 }}>
          {isTicketKey(run.key) && (
            <button
              type="button"
              className="bss-btn bss-btn--ghost bss-btn--sm"
              onClick={() => setAssignOpen(true)}
              title={`Assign another PR to ${run.key}`}
            >
              <Icon.Plus size={13} /> Assign PR…
            </button>
          )}
          <RefreshButton onClick={refresh} loading={loading} />
        </span>
      </div>

      <AssignPrModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        initialKey={run.key}
        onAssigned={refresh}
      />

      <div className={heroCls}>
        {live && <div className="bss-live-ribbon" />}
        <div style={{ flex: 1, position: "relative" }}>
          <div className="bss-hero-title">
            <span className="bss-key">{run.key}</span>
            <span style={{ color: "var(--bss-fg-subtle)" }}>·</span>
            <h1>{run.summary || "Untitled run"}</h1>
            <StatusBadge status={run.status} pulsing={live} />
            {run.mode === "test_plan" && (
              <span className="bss-badge bss-badge--blue">
                <Icon.FileText size={11} /> Test plan
              </span>
            )}
            {run.attempt > 1 && (
              <span className="bss-badge bss-badge--gray">
                <Icon.RotateCw size={11} /> Attempt {run.attempt}
              </span>
            )}
          </div>

          <div className="bss-hero-meta">
            {run.triggeredBy && (
              <span className="bss-item">
                <Avatar name={run.triggeredBy.name} size="sm" />
                Triggered by <strong>{run.triggeredBy.name}</strong>
              </span>
            )}
            <span className="bss-item">
              <Icon.Clock size={12} /> Started{" "}
              <strong>{relativeTime(run.startedAt)}</strong>
            </span>
            <span className="bss-item">
              <Icon.Activity size={12} /> Elapsed{" "}
              <strong style={{ fontFamily: "var(--bss-font-mono)" }}>
                {formatDuration(run.durationSec)}
              </strong>
            </span>
            <span className="bss-item">
              <Icon.DollarSign size={12} /> Spend{" "}
              <strong style={{ fontFamily: "var(--bss-font-mono)" }}>
                {formatMoney(totalCost)}
              </strong>
              <span style={{ color: "var(--bss-fg-subtle)" }}>
                / {formatMoney(run.budgetUsd)}
              </span>
            </span>
            {run.repoId && (
              <span className="bss-item">
                <Icon.Github size={12} />
                <span className="bss-code-inline" style={{ fontSize: 11 }}>
                  {run.repoId}
                </span>
              </span>
            )}
            <span className="bss-item">
              <Icon.GitBranch size={12} />
              {run.branch ? (
                <span className="bss-code-inline" style={{ fontSize: 11 }}>
                  {run.branch}
                </span>
              ) : (
                <span style={{ color: "var(--bss-fg-subtle)" }}>
                  no branch yet
                </span>
              )}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: 16,
              flexWrap: "wrap"
            }}
          >
            {phases.map((p, i) => {
              const c = phaseStripColor(p.status);
              const pending = p.status === "pending";
              return (
                <span
                  key={p.name || i}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    height: 22,
                    padding: "0 9px",
                    borderRadius: 999,
                    background: pending
                      ? "transparent"
                      : `color-mix(in oklab, ${c} 16%, transparent)`,
                    border: `1px solid ${
                      pending ? "var(--bss-border)" : "transparent"
                    }`,
                    fontSize: 11.5,
                    fontWeight: 500,
                    color: pending ? "var(--bss-fg-subtle)" : c
                  }}
                >
                  {p.status === "done" && <Icon.Check size={11} />}
                  {p.status === "running" && <Spinner size={9} />}
                  {p.status === "failed" && <Icon.X size={11} />}
                  {p.name}
                </span>
              );
            })}
          </div>
        </div>

        <div className="bss-hero-cta">
          {run.pr && (
            <a
              className="bss-btn bss-btn--ghost"
              href={run.pr.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon.GitPullRequest size={13} /> PR #{run.pr.number}{" "}
              <Icon.ExternalLink size={11} />
            </a>
          )}
          {live && (
            <button
              type="button"
              className="bss-btn bss-btn--danger"
              onClick={cancelRun}
              disabled={busy}
            >
              {busy ? <Spinner /> : <Icon.Square size={12} />} Cancel run
            </button>
          )}
          {!live && awaitingApproval && (
            <button
              type="button"
              className="bss-btn bss-btn--primary"
              onClick={approveRun}
              disabled={busy}
            >
              {busy ? <Spinner /> : <Icon.Check size={13} />} Approve &amp; mark
              PR ready
            </button>
          )}
          {!live && !awaitingApproval && run.mode !== "adoption" && (
            <button
              type="button"
              className="bss-btn bss-btn--primary"
              onClick={() => retryRun()}
              disabled={busy}
            >
              {busy ? <Spinner /> : <Icon.RotateCw size={13} />} Re-run
            </button>
          )}
        </div>
      </div>

      {actionError && (
        <div
          style={{
            color: "var(--bss-danger)",
            fontSize: 13,
            marginBottom: 16
          }}
        >
          {actionError}
        </div>
      )}

      {run.status === "FAILED" && run.error && (
        <div
          className="bss-card"
          style={{
            padding: 18,
            marginBottom: 24,
            borderColor: "rgba(255, 82, 71, 0.32)"
          }}
        >
          <div className="bss-error-block" style={{ margin: 0 }}>
            <div className="bss-hd">
              <Icon.AlertTriangle size={14} />
              Run failed during{" "}
              <strong>
                {(phases.find((p) => p.status === "failed") || {}).name ||
                  "an agent phase"}
              </strong>
            </div>
            {run.error}
          </div>
        </div>
      )}

      {run.groupId && (run.rationale || run.contract) && (
        <div
          className="bss-card"
          data-testid="architect-decision"
          style={{ padding: 20, marginBottom: 24 }}
        >
          <div
            className="bss-card-hd"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: 0,
              marginBottom: 14
            }}
          >
            <h3
              style={{
                fontSize: 13,
                color: "var(--bss-fg-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.06em"
              }}
            >
              Architect Decision
            </h3>
            <button
              type="button"
              className="bss-btn bss-btn--ghost bss-btn--sm"
              style={{ marginLeft: "auto" }}
              onClick={() => history.push(`/bss/groups/${run.groupId}`)}
            >
              <Icon.GitBranch size={12} /> View group
            </button>
          </div>

          {run.rationale && (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--bss-fg-subtle)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 6
                }}
              >
                Why these repos
              </div>
              <div
                style={{
                  fontSize: 13.5,
                  color: "var(--bss-fg-strong)",
                  lineHeight: 1.55
                }}
              >
                <Markdown src={run.rationale} />
              </div>
            </div>
          )}

          {Array.isArray(run.affectedRepos) && run.affectedRepos.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--bss-fg-subtle)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 6
                }}
              >
                Affected repos
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6
                }}
              >
                {run.affectedRepos.map((r) => (
                  <span
                    key={r}
                    className="bss-badge bss-badge--blue"
                    style={{ fontFamily: "var(--bss-font-mono)" }}
                  >
                    <Icon.GitBranch size={11} /> {r}
                  </span>
                ))}
              </div>
            </div>
          )}

          {run.planSlice && (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--bss-fg-subtle)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 6
                }}
              >
                This repo&apos;s plan slice
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.55 }}>
                <Markdown src={run.planSlice} />
              </div>
            </div>
          )}

          {run.contract && (
            <details>
              <summary
                style={{
                  fontSize: 11.5,
                  color: "var(--bss-fg-subtle)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  cursor: "pointer",
                  marginBottom: 6
                }}
              >
                Locked cross-repo contract
              </summary>
              <div style={{ fontSize: 13, lineHeight: 1.55, marginTop: 8 }}>
                <Markdown src={run.contract} />
              </div>
            </details>
          )}
        </div>
      )}

      {run.designContext && <DesignProofCard design={run.designContext} />}
      {run.ticketMediaContext && <TicketMediaCard media={run.ticketMediaContext} />}
      {run.slackContext && <SlackDiscussionCard slack={run.slackContext} />}
      {run.flowNote && <FlowNoteCard note={run.flowNote} />}
      {run.mode === "review" && run.reviewFindings && (
        <ReviewBreakdownCard findings={run.reviewFindings} />
      )}

      <div className="bss-detail-grid">
        {run.mode === "adoption" ? (
          <div
            className="bss-card"
            style={{
              padding: 20,
              color: "var(--bss-fg-muted)",
              fontSize: 13,
              lineHeight: 1.6
            }}
          >
            <h3
              style={{
                fontSize: 13,
                color: "var(--bss-fg-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 10
              }}
            >
              Adopted PR
            </h3>
            BSS discovered this PR via the hourly adoption poller and is
            tracking it here for visibility. No agent phases ran — the PR was
            opened by a human.
            <br />
            <br />
            BSS will only act on this PR (refresh, address comments, address
            failed checks) if a human adds the{" "}
            <code className="bss-code-inline">bss</code> label.
          </div>
        ) : (
          <div className="bss-card" style={{ overflow: "hidden" }}>
            <div
              className="bss-card-hd"
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <h3
                style={{
                  fontSize: 13,
                  color: "var(--bss-fg-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em"
                }}
              >
                Phase timeline
              </h3>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 11.5,
                  color: "var(--bss-fg-subtle)"
                }}
              >
                {phases.length} phases
              </span>
            </div>
            <div style={{ padding: "18px 20px 20px" }}>
              {phases.length === 0 ? (
                <div style={{ color: "var(--bss-fg-muted)", fontSize: 13 }}>
                  No phases recorded yet.
                </div>
              ) : (
                <div className="bss-phases">
                  {phases.map((p, i) => (
                    <PhaseRow key={p.name || i} phase={p} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="bss-side-panel">
          <div className="bss-side-card">
            <h4>Run</h4>
            <div className="bss-side-row">
              <span className="bss-k">Status</span>
              <span className="bss-v">
                <StatusBadge status={run.status} pulsing={live} />
              </span>
            </div>
            <div className="bss-side-row">
              <span className="bss-k">Mode</span>
              <span className="bss-v">
                {MODE_LABELS[run.mode] || "Implement"}
              </span>
            </div>
            {run.repoId && (
              <div className="bss-side-row">
                <span className="bss-k">Repo</span>
                <span
                  className="bss-v"
                  style={{ fontFamily: "var(--bss-font-mono)", fontSize: 12 }}
                >
                  {run.repoId}
                </span>
              </div>
            )}
            <div className="bss-side-row">
              <span className="bss-k">Attempt</span>
              <span className="bss-v">{run.attempt}</span>
            </div>
            <div className="bss-side-row">
              <span className="bss-k">Run ID</span>
              <span
                className="bss-v"
                style={{
                  fontFamily: "var(--bss-font-mono)",
                  fontSize: 11
                }}
              >
                {run.id}
              </span>
            </div>
            <div className="bss-side-row">
              <span className="bss-k">Started</span>
              <span className="bss-v">{relativeTime(run.startedAt)}</span>
            </div>
          </div>

          <div className="bss-side-card">
            <h4>Budget</h4>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span
                style={{
                  fontSize: 22,
                  fontWeight: 600,
                  color:
                    budgetPct >= 1
                      ? "var(--bss-danger)"
                      : "var(--bss-fg-strong)",
                  fontFamily: "var(--bss-font-mono)"
                }}
              >
                {formatMoney(totalCost)}
              </span>
              <span style={{ color: "var(--bss-fg-muted)", fontSize: 12 }}>
                of {formatMoney(run.budgetUsd)}
              </span>
            </div>
            <ProgressBar value={totalCost} max={run.budgetUsd} />
            <div
              style={{
                marginTop: 12,
                fontSize: 11.5,
                color: "var(--bss-fg-muted)"
              }}
            >
              {budgetPct >= 1 ? (
                <span style={{ color: "var(--bss-danger)" }}>
                  Cap exceeded · run halted
                </span>
              ) : (
                `${Math.round(budgetPct * 100)}% of cap used`
              )}
            </div>
            <div
              style={{
                marginTop: 14,
                paddingTop: 12,
                borderTop: "1px solid var(--bss-border)"
              }}
            >
              {phases
                .filter((p) => p.costUsd != null)
                .map((p, i) => (
                  <div
                    key={p.name || i}
                    className="bss-side-row"
                    style={{ padding: "3px 0", fontSize: 11.5 }}
                  >
                    <span
                      className="bss-k"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: 999,
                          background: PHASE_COLOR[p.name]
                        }}
                      />
                      {p.name}
                    </span>
                    <span
                      className="bss-v"
                      style={{
                        fontFamily: "var(--bss-font-mono)",
                        fontSize: 11.5
                      }}
                    >
                      {formatMoney(p.costUsd)}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {run.description && (
            <div className="bss-side-card">
              <h4>Ticket</h4>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--bss-fg-strong)",
                  fontWeight: 500,
                  marginBottom: 4
                }}
              >
                {run.key}
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: "var(--bss-fg-muted)",
                  lineHeight: 1.5,
                  maxHeight: 160,
                  overflow: "auto"
                }}
              >
                {run.description}
              </div>
              {isTicketKey(run.key) && (
                <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                  <button
                    type="button"
                    className="bss-btn bss-btn--ghost bss-btn--sm"
                    onClick={() =>
                      history.push(
                        `/bss/tickets/${encodeURIComponent(run.key)}`
                      )
                    }
                  >
                    <Icon.Ticket size={12} /> All runs
                  </button>
                </div>
              )}
            </div>
          )}

          {run.mode !== "adoption" && (
            <div className="bss-side-card">
              <h4>Actions</h4>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6
                }}
              >
                <button
                  type="button"
                  className="bss-btn bss-btn--ghost bss-btn--sm"
                  style={{ justifyContent: "flex-start" }}
                  onClick={() => retryRun("implement")}
                  disabled={busy}
                >
                  <Icon.RotateCw size={12} /> Re-run
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <BrowserVerifyVerdicts
        browserVerify={run.browserVerify}
        ticketKey={run.key || ""}
      />
      <ReleaseCutPanel releaseCut={run.releaseCut} />
      <LensBreakdown findings={run.reviewFindings} />
    </Shell>
  );
}
